import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { User, TrendingUp, MessageSquare, Calendar, Award, FileText, Filter, Clock, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import CarnetNotesFormateur from '../components/CarnetNotesFormateur';
import CarnetNotesTuteur from '../components/CarnetNotesTuteur';
import CompetencesPrerequisConsultation from '../components/CompetencesPrerequisConsultation';

const SuiviStagiaireEntreprise = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [notes, setNotes] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [heuresParCategorie, setHeuresParCategorie] = useState({});
  const [autonomiesValidees, setAutonomiesValidees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCarnet, setActiveCarnet] = useState('tuteur');
  const [customEpreuves, setCustomEpreuves] = useState([]);
  
  // Filtres pour le graphique
  const [selectedCategorie, setSelectedCategorie] = useState('all');
  const [selectedItem, setSelectedItem] = useState('all');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  // Structure hiérarchique des catégories (même que Notation.js)
  const categoriesStructure = {
    'Briefing': ['BRIEFING VNM', 'BRIEFING Engins tractés', 'BRIEFING ESH', 'BRIEFING PNM', 'BRIEFING JET À BRAS'],
    'Encadrement': ['VNM', 'Engins tractés', 'ESH', 'PNM', 'JET À BRAS'],
    'Sécurité': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau', 'Engins tractés'],
    'Pédagogie': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau'],
    'Aisance': ['VNM', 'Jet à bras', 'PNM', 'Engins tractés', 'Bateau'],
    'Procédure d\'intervention': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau'],
    'Animation': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Engins tractés'],
    'Compétences Générales': [
      'Entretien rangement du matériel',
      'Implication générale'
    ],
    'Prérequis': ['Parcours jet à bras', 'Parcours jet à selle', 'Parcours engins tractés'],
    'Épreuves Formatives': [
      'Réglementation',
      'Public',
      'Physio',
      'Anatomie',
      'Météo',
      'Message de secours',
      ...customEpreuves
    ]
  };

  useEffect(() => {
    loadStagiaires();
    loadCustomEpreuves();
  }, []);

  useEffect(() => {
    if (selectedStagiaire) {
      loadStagiaireData();
    }
  }, [selectedStagiaire]);

  // Réinitialiser l'item quand la catégorie change
  useEffect(() => {
    setSelectedItem('all');
  }, [selectedCategorie]);

  const loadCustomEpreuves = async () => {
    try {
      const response = await axios.get('/epreuves-formatives');
      setCustomEpreuves(response.data.map(e => e.sujet));
    } catch (error) {
    }
  };

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      if (response.data.length > 0) {
        setSelectedStagiaire(response.data[0].user_id);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadStagiaireData = async () => {
    try {
      const [notesRes, compRes, heuresRes, autoRes] = await Promise.all([
        axios.get(`/notes/stagiaire/${selectedStagiaire}`),
        axios.get(`/competences/stagiaire/${selectedStagiaire}`),
        axios.get(`/heures-encadrement/stagiaire/${selectedStagiaire}`),
        axios.get(`/autonomies-en-attente/stagiaire/${selectedStagiaire}`).catch(() => ({ data: [] })),
      ]);
      setNotes(notesRes.data);
      setCompetences(compRes.data);
      const autoData = Array.isArray(autoRes.data) ? autoRes.data : [];
      setAutonomiesValidees(autoData.filter(a => a.stagiaire_reponse === true));
      
      // Calculer heures par catégorie ET sous-catégorie
      const heuresData = heuresRes.data;
      const heures = Array.isArray(heuresData) ? heuresData : (heuresData?.heures || []);
      
      if (Array.isArray(heures)) {
        const cumul = {};
        heures.forEach(h => {
          const cat = h.categorie || 'Autre';
          const sousCat = h.sous_categorie || 'Général';
          
          if (!cumul[cat]) cumul[cat] = { total: 0, sousCat: {} };
          cumul[cat].total += h.heures || 0;
          
          if (!cumul[cat].sousCat[sousCat]) cumul[cat].sousCat[sousCat] = 0;
          cumul[cat].sousCat[sousCat] += h.heures || 0;
        });
        setHeuresParCategorie(cumul);
      }
    } catch (error) {
    }
  };

  const currentStagiaire = stagiaires.find(s => s.user_id === selectedStagiaire);

  // Filtrer les notes par période
  const notesFiltered = notes.filter(n => {
    const noteDate = new Date(n.date);
    if (dateDebut && new Date(dateDebut) > noteDate) return false;
    if (dateFin && new Date(dateFin) < noteDate) return false;
    return true;
  });

  // Items disponibles selon la catégorie sélectionnée
  const availableItems = selectedCategorie !== 'all' 
    ? categoriesStructure[selectedCategorie] || []
    : [];

  // Préparer les données pour le graphique
  const prepareChartData = () => {
    let filteredNotes = notesFiltered;
    
    // Filtrer par catégorie
    if (selectedCategorie !== 'all') {
      filteredNotes = filteredNotes.filter(n => n.categorie === selectedCategorie);
    }
    
    // Filtrer par item
    if (selectedItem !== 'all') {
      filteredNotes = filteredNotes.filter(n => n.item === selectedItem);
    }

    const grouped = filteredNotes.reduce((acc, note) => {
      const date = note.date?.split('T')[0] || '';
      if (!date) return acc;
      if (!acc[date]) acc[date] = { vals: [], details: [] };
      acc[date].vals.push(note.note);
      acc[date].details.push({
        categorie: note.categorie || 'Autre',
        item: note.item || '',
        note: note.note,
        commentaire: note.commentaire || ''
      });
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        fullDate: date,
        note: parseFloat((data.vals.reduce((a, b) => a + b, 0) / data.vals.length).toFixed(1)),
        details: data.details
      }))
      .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  };

  // Grouper les notes par catégorie (avec filtres appliqués)
  const getFilteredNotesByCategorie = () => {
    let filtered = notesFiltered;
    if (selectedCategorie !== 'all') {
      filtered = filtered.filter(n => n.categorie === selectedCategorie);
    }
    if (selectedItem !== 'all') {
      filtered = filtered.filter(n => n.item === selectedItem);
    }
    return filtered.reduce((acc, note) => {
      if (!acc[note.categorie]) acc[note.categorie] = [];
      acc[note.categorie].push(note);
      return acc;
    }, {});
  };

  const notesByCategorie = getFilteredNotesByCategorie();

  // Filtrer les appréciations du tuteur (avec commentaires)
  const appreciationsTuteur = competences.filter(c => c.appreciation && c.appreciation.trim() !== '');

  // Moyenne générale
  const moyenneGenerale = notes.length > 0 
    ? (notes.reduce((sum, n) => sum + n.note, 0) / notes.length).toFixed(1)
    : 0;

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="suivi-stagiaire-entreprise">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Suivi du Stagiaire</h1>
        <p className="text-slate-600">Consultez les notes, commentaires et progression de votre stagiaire</p>
      </div>

      {/* Sélection Stagiaire */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="space-y-2">
          <Label htmlFor="stagiaire">Stagiaire</Label>
          <select
            id="stagiaire"
            value={selectedStagiaire}
            onChange={(e) => setSelectedStagiaire(e.target.value)}
            className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="select-stagiaire"
          >
            {stagiaires.length === 0 ? (
              <option value="">Aucun stagiaire assigné</option>
            ) : (
              stagiaires.map((s) => (
                <option key={s.user_id} value={s.user_id}>
                  {s.user_info?.prenom} {s.user_info?.nom}
                </option>
              ))
            )}
          </select>
        </div>

        {currentStagiaire && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg flex items-center space-x-4">
            <div className="w-12 h-12 bg-[#8DC63F]/10 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#0F172A]">
                {currentStagiaire.user_info?.prenom} {currentStagiaire.user_info?.nom}
              </p>
              <p className="text-sm text-slate-600">
                {currentStagiaire.centre_formation} - {currentStagiaire.statut}
              </p>
            </div>
            <div className="text-right bg-white px-4 py-2 rounded-lg border border-slate-200">
              <p className="text-2xl font-bold text-[#8DC63F]">{moyenneGenerale}/10</p>
              <p className="text-xs text-slate-600">Moyenne générale</p>
            </div>
          </div>
        )}
      </div>

      {/* Filtres de période et catégorie/item */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-[#0F172A]">Filtres</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Période */}
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Du
            </label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="date-debut"
            />
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Au
            </label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="date-fin"
            />
          </div>
          {/* Catégorie */}
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-slate-500">Catégorie</label>
            <select
              value={selectedCategorie}
              onChange={(e) => {
                setSelectedCategorie(e.target.value);
                setSelectedItem('all');
              }}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-categorie"
            >
              <option value="all">Toutes les catégories</option>
              {Object.keys(categoriesStructure).map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          {/* Article/Item */}
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-slate-500">
              Article {selectedCategorie !== 'all' && <span className="text-slate-400">({selectedCategorie})</span>}
            </label>
            <select
              value={selectedItem}
              onChange={(e) => setSelectedItem(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-item"
              disabled={selectedCategorie === 'all'}
            >
              <option value="all">{selectedCategorie === 'all' ? 'Sélectionnez une catégorie' : 'Tous les articles'}</option>
              {availableItems.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Bouton reset */}
        {(dateDebut || dateFin || selectedCategorie !== 'all' || selectedItem !== 'all') && (
          <button 
            onClick={() => { setDateDebut(''); setDateFin(''); setSelectedCategorie('all'); setSelectedItem('all'); }}
            className="mt-3 text-xs text-[#8DC63F] hover:underline"
            data-testid="reset-filters"
          >
            Réinitialiser les filtres
          </button>
        )}
      </div>

      {/* Graphique de Progression */}
      {notes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="h-6 w-6 text-[#8DC63F]" />
            <h2 className="text-xl font-display font-semibold text-[#0F172A]">
              Progression des Notes
              {selectedCategorie !== 'all' ? ` - ${selectedCategorie}` : ''}
              {selectedItem !== 'all' ? ` / ${selectedItem}` : ''}
            </h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={prepareChartData()}>
                <ReferenceArea y1={0} y2={4} fill="#EF4444" fillOpacity={0.1} />
                <ReferenceArea y1={4} y2={7} fill="#F59E0B" fillOpacity={0.1} />
                <ReferenceArea y1={7} y2={10} fill="#22C55E" fillOpacity={0.1} />
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0].payload;
                  const details = data.details || [];
                  return (
                    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg max-w-xs">
                      <p className="font-semibold text-sm text-slate-800 mb-1">{data.date} &mdash; <span className="text-[#8DC63F]">{data.note}/10</span></p>
                      {details.map((d, i) => (
                        <div key={i} className="mb-1 last:mb-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs"><span className="font-medium text-slate-700">{d.categorie}</span>{d.item && <span className="text-slate-400"> / {d.item}</span>}</span>
                            <span className="text-xs font-bold">{d.note}/10</span>
                          </div>
                          {d.commentaire && <p className="text-[10px] text-slate-400 truncate">{d.commentaire.substring(0, 70)}{d.commentaire.length > 70 ? '...' : ''}</p>}
                        </div>
                      ))}
                    </div>
                  );
                }} />
                <Line 
                  type="monotone" 
                  dataKey="note" 
                  stroke="#8DC63F" 
                  strokeWidth={3}
                  dot={{ fill: '#8DC63F', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
              <span className="text-slate-600">Non compétent (0-4)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-100 border border-orange-300 rounded"></div>
              <span className="text-slate-600">En cours (4-7)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
              <span className="text-slate-600">Acquis (7-10)</span>
            </div>
          </div>
        </div>
      )}

      {/* Carnets de Notes - 2 Onglets */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveCarnet('tuteur')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 transition-colors ${
              activeCarnet === 'tuteur'
                ? 'bg-orange-50 border-b-2 border-orange-500 text-orange-600'
                : 'hover:bg-slate-50 text-slate-600'
            }`}
          >
            <Award className="h-5 w-5" />
            <span className="font-medium">Carnet Tuteur</span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeCarnet === 'tuteur' ? 'bg-orange-100' : 'bg-slate-200'
            }`}>{competences.length}</span>
          </button>
          <button
            onClick={() => setActiveCarnet('formateur')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 transition-colors ${
              activeCarnet === 'formateur'
                ? 'bg-[#8DC63F]/10 border-b-2 border-[#8DC63F] text-[#8DC63F]'
                : 'hover:bg-slate-50 text-slate-600'
            }`}
          >
            <BookOpen className="h-5 w-5" />
            <span className="font-medium">Carnet Formateur</span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeCarnet === 'formateur' ? 'bg-[#8DC63F]/20' : 'bg-slate-200'
            }`}>{notes.length}</span>
          </button>
        </div>
        <div className="p-6">
          {activeCarnet === 'tuteur' ? (
            <CarnetNotesTuteur 
              evaluations={competences} 
              heuresParCategorie={heuresParCategorie}
              autonomiesValidees={autonomiesValidees}
              title="Compétences évaluées par le Tuteur"
            />
          ) : (
            <CarnetNotesFormateur 
              notes={notes} 
              title="Notes du Centre de Formation"
              hidePrivateNotes={true}
            />
          )}
        </div>
      </div>

      {/* Prérequis et Blocs de Compétences */}
      {selectedStagiaire && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-[#8DC63F]" />
            Prérequis et Blocs de Compétences
          </h2>
          <CompetencesPrerequisConsultation stagiaireId={selectedStagiaire} hideEpmsp={true} />
        </div>
      )}
    </div>
  );
};

export default SuiviStagiaireEntreprise;
